import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from 'hooks/useFeatureFlag';

export interface ViewAsInfo {
  viewAsEmail: string | undefined;
  viewAsBranchId: string | undefined;
  isViewingAs: boolean;
  /**
   * Spread into any filter/params object to forward view-as context to the API.
   * e.g. `{ ...myFilters, ...viewAs.filterParams }`
   */
  filterParams: Record<string, string>;
}

/**
 * Reads view-as context from the current URL's search params.
 *
 * Pages opened via bucket-click from the homepage receive
 * `?view_as_email=` or `?view_as_branch_id=` in their URL.
 * This hook normalises those into a single, consistent shape that
 * can be plugged into any API call or React Query key.
 */
export function useViewAs(): ViewAsInfo {
  const [searchParams] = useSearchParams();

  const isViewAsEnabled = useFeatureFlag('enable-desktop-view-as');
  const urlEmail = searchParams.get('view_as_email') ?? undefined;
  const urlBranchId = searchParams.get('view_as_branch_id') ?? undefined;
  const viewAsEmail = isViewAsEnabled ? urlEmail : undefined;
  const viewAsBranchId = isViewAsEnabled ? urlBranchId : undefined;
  const isViewingAs = !!(viewAsEmail || viewAsBranchId);

  const filterParams = useMemo<Record<string, string>>(() => {
    const p: Record<string, string> = {};
    if (viewAsEmail) p.view_as_email = viewAsEmail;
    if (viewAsBranchId) p.view_as_branch_id = viewAsBranchId;
    return p;
  }, [viewAsEmail, viewAsBranchId]);

  return { viewAsEmail, viewAsBranchId, isViewingAs, filterParams };
}
